import '@fortawesome/fontawesome-free/css/all.min.css';
import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Stack } from 'react-bootstrap';
import { CiShare1 } from 'react-icons/ci';
import { FaDownload } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../components/tables/cellComponents/LinkCell';
import YesNoCell from '../../../components/tables/cellComponents/YesNoCell';
import { useToast } from '../../../context/ToastContext';
import { userCanEditCreateDelete_Documents } from '../../../features/dash/views/content/Documents/DocumentsView';
import {
  hasPrimaryRecipe,
  userCanDelete_LessonPlan,
  userCanEditCreateDelete_LessonPlan
} from '../../../features/dash/views/content/LessonPlans/LessonPlansView';
import {
  userCanDelete_Recipes,
  userCanEditCreateDelete_Recipes,
  userCanView_Recipes
} from '../../../features/dash/views/content/Recipes/RecipesView';
import { baseUrl } from '../../../services/api';
import { authHeaderToken } from '../../../services/auth-header';
import { useDeleteDocumentsMutation } from '../../../services/endpoints/content/document';
import { useDeleteJokeMutation } from '../../../services/endpoints/content/joke';
import { useDeleteJokeCategoryMutation } from '../../../services/endpoints/content/jokeCategory';
import { useDeleteLessonPlanMutation } from '../../../services/endpoints/content/lessonPlan';
import { useDeleteRecipeMutation } from '../../../services/endpoints/content/recipe';
import { useDeleteThemeMutation } from '../../../services/endpoints/content/theme';
import { getCurrentUser } from '../../../services/helper';
import { Document, Joke, JokeCategory } from '../../../types/content.type';
import { LessonPlan, Recipe } from '../../../types/lessonPlan.type';
import { canEditThemes, canViewThemes } from '../../rbac';
import { generateFileUrlSegment, getErrorMessage } from '../../utils';

const DeleteLessonPlanButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteLessonPlan, { isLoading }] = useDeleteLessonPlanMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteLessonPlan(id).unwrap();
      addToast('Lesson plan deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Lesson Plan"
        message="Are you sure you want to delete this lesson plan?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeleteRecipeButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteRecipe, { isLoading }] = useDeleteRecipeMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteRecipe(id).unwrap();
      addToast('Recipe deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Recipe"
        message="Are you sure you want to delete this recipe?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeleteDocumentButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteDocument, { isLoading }] = useDeleteDocumentsMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteDocument({ id }).unwrap();
      addToast('Document deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Document"
        message="Are you sure you want to delete this document?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeleteThemeButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteTheme, { isLoading }] = useDeleteThemeMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteTheme(id).unwrap();
      addToast('Theme deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Theme"
        message="Are you sure you want to delete this theme?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeleteJokeButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteJoke, { isLoading }] = useDeleteJokeMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteJoke(id).unwrap();
      addToast('Joke deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Joke"
        message="Are you sure you want to delete this joke?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeleteJokeCategoryButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteJokeCategory, { isLoading }] = useDeleteJokeCategoryMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteJokeCategory(id).unwrap();
      addToast('Joke category deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Joke Category"
        message="Are you sure you want to delete this joke category?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const currentUser = getCurrentUser();

export const recipesColumns: ColumnDef<Recipe>[] = [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Image',
    accessorKey: '',
    cell: ({ row }) => (
      <img
        src={`${generateFileUrlSegment(row.original?.recipe_images[0]?.id, row.original?.recipe_images[0]?.s3_base_url, 'thumb')}/${row.original?.recipe_images[0]?.image_file_name}`}
        width="50"
        height="50"
        alt={row.original.title}
      />
    )
  },
  {
    header: 'Recipe Type',
    accessorKey: 'recipe_type'
  },
  {
    header: 'Title',
    accessorKey: 'title'
  },
  {
    header: 'Live',
    id: 'live',
    enableSorting: false,
    cell: ({ row }) => (
      <a href={`/recipes/${row.original.slug || row.original.id}`}>
        <CiShare1
          size={20}
        />
      </a>
    )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        {userCanView_Recipes(currentUser) && (
          <LinkCell
            content="View"
            url={`/admin/content/recipes/${row.original.id}`}
            underline
          />
        )}

        {userCanEditCreateDelete_Recipes(currentUser) && (
          <LinkCell
            content="Edit"
            url={`/admin/content/recipes/${row.original.id}/edit`}
            underline
          />
        )}

        {userCanDelete_Recipes(currentUser) && (
          <DeleteRecipeButton id={row.original.id} />
        )}
      </Stack>
    )
  }
];

export const documentColumns: ColumnDef<Document>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/content/documents/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Title',
    accessorKey: 'title'
  },
  {
    header: 'Name',
    accessorKey: 'file_file_name'
  },
  {
    header: 'Download',
    accessorKey: 'file_file_name',
    cell: ({ row }) => (
      <span
        onClick={async () => {
          const token = authHeaderToken();
          const response = await fetch(`${baseUrl}documents/${row.original.id}/download`, {
            headers: { 'x-access-token': token ?? '' }
          });
          const { url } = await response.json();
          window.open(url, '_blank');
        }}
        className="text-info fw-bolder"
        style={{ fontSize: '.875rem', cursor: 'pointer' }}
      >
        <FaDownload />
      </span>
    )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/content/documents/${row.original.id}`}
          underline
        />

        {userCanEditCreateDelete_Documents(currentUser) && (
          <>
            <LinkCell
              content="Edit"
              url={`/admin/content/documents/${row.original.id}/edit`}
              underline
            />
            <DeleteDocumentButton id={row.original.id} />
          </>
        )}
      </Stack>
    )
  }
];

export const lessonPlanColumns: ColumnDef<LessonPlan>[] = [
  {
    header: 'Id',
    id: 'lesson_plan_id',
    accessorKey: 'id',
    enableSorting: true
  },
  {
    header: 'Image',
    id: 'image',
    enableSorting: false,
    cell: ({ row }) => (
      <img
        src={`${row.original.id}.jpg`}
        alt=""
      />
    )
  },
  {
    header: 'Name',
    id: 'lesson_plan_name',
    accessorKey: 'name'
  },
  {
    header: 'Live',
    id: 'live',
    enableSorting: false,
    cell: ({ row }) => (
      <>
        {hasPrimaryRecipe(row.original) ? (
            <a href={`/lesson-plans/${row.original.slug}`}>            <CiShare1
              size={20}
              color="#4A90E2"
            />
          </a>
        ) : (
          <>N/A</>
        )}
      </>
    )
  },
  {
    header: () => '',
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/content/lesson-plans/${row.original.id}`}
          underline
        />

        {userCanEditCreateDelete_LessonPlan(currentUser) && (
          <LinkCell
            content="Edit"
            url={`/admin/content/lesson-plans/${row.original.id}/edit`}
            underline
          />
        )}

        {userCanDelete_LessonPlan(currentUser) && (
          <DeleteLessonPlanButton id={row.original.id} />
        )}
      </Stack>
    )
  }
];

export const themesColumns: ColumnDef<Document>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/content/themes/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Is Public',
    accessorKey: 'is_public',
    cell: ({ row }) => <YesNoCell value={row.original?.is_public} />
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        {canViewThemes(currentUser) && (
          <LinkCell
            content="View"
            url={`/admin/content/themes/${row.original.id}`}
            underline
          />
        )}

        {canEditThemes(currentUser) && (
          <>
            <LinkCell
              content="Edit"
              url={`/admin/content/themes/${row.original.id}/edit`}
              underline
            />
            <DeleteThemeButton id={row.original.id} />
          </>
        )}
      </Stack>
    )
  }
];

export const jokesColumns: ColumnDef<Joke>[] = [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Joke Category',
    cell: ({ row }) => (
      <LinkCell
        content={row.original.joke_category?.name}
        url={`/admin/content/joke-categories/${row.original.joke_category_id}`}
        underline
      />
    )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/content/jokes/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/content/jokes/${row.original.id}/edit`}
          underline
        />
        <DeleteJokeButton id={row.original.id} />
      </Stack>
    )
  }
];

export const jokeCategoriesColumns: ColumnDef<JokeCategory>[] = [
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/content/joke-categories/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/content/joke-categories/${row.original.id}/edit`}
          underline
        />
        <DeleteJokeCategoryButton id={row.original.id} />
      </Stack>
    )
  }
];
